import _, {compact} from 'lodash';
import React, {Component, createRef, RefObject} from 'react';
import styled from 'styled-components';

import {ContactSourcesEnum} from '../../../../../libs/shared/core/src/models/api/contactModel';
import {RecipientRolesEnum} from '../../../../../libs/shared/core/src/models/api/recipientModel';
import {reportToBugsnag} from '../../../../../libs/shared/core/src/services/bugsnag/bugsnag';
import {buildContactString} from '../../../../helpers/models/contactHelpers';
import {AddRecipients, parseRecipient, RemoveRecipients} from '../../../../helpers/models/recipientHelpers';
import {VisualRecipientModel} from '../../../../models/api/visualRecipientModel';
import {RecipientTokenContainer} from '../tokens/recipientTokenContainer';
import {TokensEditor} from '../tokens/tokensEditor';
import {
  RecipientsDragBeginHandler,
  RecipientsDragEndHandler,
  RecipientsDragSource,
} from './recipientsDragSource';
import {RecipientsDropdownContainer} from './recipientsDropdownContainer';
import {RecipientsDropHandler, RecipientsDropTarget} from './recipientsDropTarget';

/*
 * Constants.
 */

const delimiterRegexp = /[,;\t\n]+/;

/*
 * Props.
 */

interface RecipientsEditorProps {
  shouldFocus?: boolean;
  focusKey?: unknown;
  namespace: string;
  source: ContactSourcesEnum;
  role: RecipientRolesEnum;
  recipients: ReadonlyArray<VisualRecipientModel>;
  addRecipients: AddRecipients;
  removeRecipients: RemoveRecipients;
  defaultNumberPrefix?: string;
  sendAddressDomain?: string;
}

/*
 * Style.
 */

const StyledTokenEditor = styled(TokensEditor)`
  color: ${(p) => p.theme.greys.shade80};
`;

/*
 * Component.
 */

interface RecipientsEditorState {
  text: string;
  isDropdownVisible: boolean;
}

export class RecipientsEditor extends Component<RecipientsEditorProps, RecipientsEditorState> {
  constructor(props: RecipientsEditorProps) {
    super(props);

    this.tokensEditorRef = createRef<TokensEditor>();
    this.state = {
      text: '',
      isDropdownVisible: true,
    };
  }

  private readonly tokensEditorRef: RefObject<TokensEditor>;

  private hasFocus() {
    const tokensEditor = this.tokensEditorRef.current;
    return Boolean(tokensEditor && tokensEditor.hasFocus());
  }

  private focusAndMoveToEnd() {
    const tokensEditor = this.tokensEditorRef.current;
    if (!tokensEditor) {
      return;
    }

    tokensEditor.focus();
    tokensEditor.moveToEnd();
  }

  /*
   * Lifecycle.
   */

  componentDidUpdate(previousProps: Readonly<RecipientsEditorProps>) {
    const {shouldFocus, focusKey} = this.props;

    // When the focus key is bumped, re-focus if needed.
    if (previousProps.focusKey !== focusKey && shouldFocus) {
      this.focusAndMoveToEnd();
    }
  }

  /*
   * Event handlers.
   */

  private readonly onTextChange = (newText: string) => {
    const hasFocus = this.hasFocus();
    const remainingText = this.addRecipientsFromText(newText, hasFocus);

    this.setState({text: remainingText, isDropdownVisible: true}, () => {
      if (hasFocus && newText !== remainingText) {
        this.focusAndMoveToEnd();
      }
    });

    // This used to happen with Slate on Firefox, make sure it doesn't happen anymore with a plain input.
    if (newText.includes('\b')) {
      reportToBugsnag(new Error('Recipients editor text contains backspace'));
    }
  };

  private readonly onTextBlur = () => {
    const {text} = this.state;
    const remainingText = this.addRecipientsFromText(text, false);

    this.setState({text: remainingText, isDropdownVisible: false});
  };

  // TODO: Fix ever more spaces added after delimiters on every key stroke if there are invalid tokens present.
  // TODO: Fix token is not removed when the field is blurred the only token is a recipient that already exists.
  // TODO: Fix valid but partial token removed from underneath the cursor if there are any invalid tokens present.
  private addRecipientsFromText(text: string, hasFocus: boolean) {
    const minTokens = hasFocus ? 2 : 1;

    // Split to check if we need to look for recipients.
    const tokens = text.split(delimiterRegexp);
    if (tokens.length < minTokens) {
      return text;
    }

    // If a delimiter was found, look for recipients.
    const {source, role, recipients, addRecipients, defaultNumberPrefix} = this.props;
    const newRecipients = tokens.map((token) => {
      // Try to parse the token as a recipient.
      const recipient = parseRecipient(source, role, token, {defaultNumberPrefix});
      if (!recipient) {
        return null;
      }

      // Make sure that we don't already have this recipient.
      if (recipients.some((r) => r.contact.uid === recipient.contact.uid)) {
        return null;
      }

      // Otherwise, keep it.
      return recipient;
    });

    // Add the new recipients to the collection.
    const recipientsToAdd = compact(newRecipients);
    if (recipientsToAdd.length === 0) {
      return text;
    }

    addRecipients(recipientsToAdd);

    // Find the tokens that weren't added.
    const invalidTokens = tokens.filter((_t, index) => !newRecipients[index]);
    return invalidTokens.join(', ');
  }

  private readonly onRemoveRecipientsClick: (arg: ReadonlyArray<number>) => void = (recipientIndexes) => {
    const recipientsToRemove = _(recipientIndexes)
      .map((index) => this.props.recipients[index])
      .map((visualRecipient) => ({uid: visualRecipient.contact.uid, role: visualRecipient.role}))
      .compact()
      .value();

    this.props.removeRecipients(recipientsToRemove);
  };

  private readonly onEditRecipientClick: (arg: number) => void = (recipientIndex) => {
    // Find the recipient to edit.
    const recipientToEdit = this.props.recipients[recipientIndex];
    if (!recipientToEdit) {
      return;
    }

    // If one was found, remove it from the list.
    this.onRemoveRecipientsClick([recipientIndex]);

    // Then update + focus the input.
    this.setState({text: recipientToEdit.contact.handle}, () => {
      this.focusAndMoveToEnd();
    });
  };

  private readonly insertRecipients: (arg: ReadonlyArray<VisualRecipientModel>) => void = (recipients) => {
    // Add the recipient to the list.
    const {addRecipients} = this.props;
    addRecipients(recipients);

    // Clear the editor.
    this.setState({text: ''});
  };

  private readonly onDragBegin: RecipientsDragBeginHandler = () => {
    const tokensEditor = this.tokensEditorRef.current;
    if (!tokensEditor) {
      return [];
    }

    return _(tokensEditor.getDragTokenIndexes())
      .map((index) => this.props.recipients[index])
      .compact()
      .value();
  };

  private readonly onDragEnd: RecipientsDragEndHandler = (recipientUids) => {
    // Remove the recipients that were dragged out of this field.
    const recipients = this.props.recipients.filter((r) => recipientUids.includes(r.contact.uid));
    this.props.removeRecipients(recipients.map((r) => ({uid: r.contact.uid, role: r.role})));
  };

  private readonly onDrop: RecipientsDropHandler = (recipients) => {
    // Collect the recipients that are not in this field.
    const existingUids = new Set<string>(this.props.recipients.map((r) => r.contact.uid));
    const draggedRecipients = recipients.filter((r) => !existingUids.has(r.contact.uid));

    // Add new recipients with this field's role.
    const {role, addRecipients} = this.props;
    const droppedRecipients = draggedRecipients.map((recipient) => ({
      ...recipient,
      role,
    }));
    addRecipients(droppedRecipients);

    // Return the result.
    const droppedUids = droppedRecipients.map((r) => r.contact.uid);
    return {droppedRecipientUids: droppedUids};
  };

  private readonly onDropdownRequestClose = () => {
    this.setState({isDropdownVisible: false});
  };

  /*
   * Render.
   */

  render() {
    const {shouldFocus, recipients} = this.props;
    const recipientUids = recipients.map((r) => r.contact.uid);

    return (
      <>
        <RecipientsDragSource onDragBegin={this.onDragBegin} onDragEnd={this.onDragEnd}>
          <RecipientsDropTarget onDrop={this.onDrop}>
            <StyledTokenEditor
              ref={this.tokensEditorRef}
              shouldFocus={shouldFocus}
              tokenIds={recipientUids}
              renderToken={this.renderRecipient}
              renderTokenForCopy={this.renderRecipientForCopy}
              onRemoveTokensClick={this.onRemoveRecipientsClick}
              onEditTokenClick={this.onEditRecipientClick}
              text={this.state.text}
              onTextChange={this.onTextChange}
              onBlur={this.onTextBlur}
              inputId={`recipient-editor-${this.props.role}`}
            />
          </RecipientsDropTarget>
        </RecipientsDragSource>
        {this.maybeRenderDropdown()}
      </>
    );
  }

  private readonly renderRecipient = (index: number, isSelected: boolean) => {
    const recipient = this.props.recipients[index];
    const {sendAddressDomain} = this.props;

    return (
      <RecipientTokenContainer
        recipient={recipient}
        isSelected={isSelected}
        sendAddressDomain={sendAddressDomain}
      />
    );
  };

  private readonly renderRecipientForCopy = (index: number) => {
    const recipient = this.props.recipients[index];
    return buildContactString(recipient);
  };

  private maybeRenderDropdown() {
    const tokensEditor = this.tokensEditorRef.current;
    if (!tokensEditor) {
      return null;
    }

    // Check if the dropdown should be displayed.
    const {text, isDropdownVisible} = this.state;

    // Extract the current text.
    const query = text.trim();

    // Render a dropdown with the query we found.
    return (
      <RecipientsDropdownContainer
        {...this.props}
        anchor={tokensEditor.getAnchor()}
        isDropdownVisible={isDropdownVisible}
        query={query}
        onRecipientsSelect={this.insertRecipients}
        onRequestClose={this.onDropdownRequestClose}
      />
    );
  }
}
